// TODO: update to show completed user rank information

import React from "react";
import PropTypes from "prop-types";
import { Portal } from "react-portal";

import { Settings } from "@salesforce/design-system-react";
import IconContext from "components/utils/IconContext";
import Sharing from "./Sharing";
import RewardConfetti from "./RewardConfetti";
import WebComponent from "../WebComponent";
import { t } from 'lib/I18n';

Settings.setAppElement("#main-wrapper");

export default class TrailCompletedModal extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    sharing: PropTypes.object,
    celebrationHeader: PropTypes.string.isRequired,
    footerCta: PropTypes.string.isRequired,
    trailsLink: PropTypes.string.isRequired,
    shareCta: PropTypes.string.isRequired,
    isOpen: PropTypes.bool,
    inProgressTrails: PropTypes.array,
    preferences: PropTypes.object,
  };

  state = {
    isOpen: this.props.isOpen,
    showFooterLinks: true
  };

  modal = React.createRef();

  closeModal = () => {
    this.setState({ isOpen: false });
    this.resetFocus();
  };

  componentDidMount() {
    this.setLastPageCtaProp();

    if (this.modal.current) {
      this.modal.current.addEventListener("requestclose", this.closeModal);
      this.modal.current.addEventListener("statechange", this.handleCollectionStateChange);
      window.addEventListener("lpstatechange", this.handleLpStateChange);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.isOpen && this.state.isOpen && window.emitter?.emit) {
      /**
       * Notes:
       * - Timeout exists to ensure that the th-streaks-manager's API call finishes before the event
       * is attempted. Otherwise, the emitter's event listener may have not been wired up yet.
       * For localhost testing, you might need to increase this from 500 to ~800ms.
       *
       * - Streaks celebrations should only happen on the unit page before this modal
       * is opened for Trail-complete celebrations. So the Streak-related emitter below
       * exists in case the user returns to this page at a later point in time, after their Streak
       * has been extended by completing the Trail.
       */
      setTimeout(() => {
        window.emitter.emit("custom", {
          name: "award_earned",
          payload: {},
        });
      }, 500);
    }
  }

  lastPageCtaConfig = () => {
    if(this.props.inProgressTrails.length && this.props.trailsLink) {
      return {
        href: "/trails",
        label: this.props.trailsLink,
      };
    } else {
      return {
        href: "/today",
        label: t('reward.button.explore_more'),
        header: t('reward.encourage'),
        description: t('reward.button.explore_more_description')
      };
    }
  }

  setLastPageCtaProp = () => {
    const lastPageCta = this.lastPageCtaConfig();

    if (this.modal.current) {
      this.modal.current.lastPageCta = lastPageCta ? { ...lastPageCta } : null;
    }
  };

  handleLpStateChange = (event) => {
    if (event.detail.state === "open") {
      this.setState({ isOpen: true });
    }
  };

  handleCollectionStateChange = (event) => {
    if (event.detail.state === "empty") {
      // Ensure the last page is removed if th-content-collection (th-recommended-trails) returns an empty state after API call
      this.modal.current.removeLastPageSlot();
      event.stopPropagation();
    }
  };

  resetFocus = () => {
    document.querySelector(".th-content-container").focus();
  };

  renderSharing = () => {
    return (
      <div>
        <Sharing
          appId={this.props.sharing.fbAppId}
          content={{
            title: this.props.title,
            description: this.props.description
          }}
          public={true}
          facebookText={this.props.sharing.facebook_share_text}
          twitterText={this.props.sharing.twitter_share_text}
        />
      </div>
    );
  };

  renderConfetti = () => {
    if (!this.state.isOpen) {
      return null;
    }

    return (
      <Portal>
        <RewardConfetti colorScheme={this.props.colorScheme} />
      </Portal>
    );
  };

  renderFooterLinks = () => {
    if (this.props.inProgressTrails.length) {
      return (
        <div className="trail-completed-modal_footer-header" style={{ margin: "10px 0 20px 0" }}>
          <div>{this.props.footerCta}</div>
          <a href="/trails">{this.props.trailsLink}</a>
        </div>
      );
    }

    if (this.state.showFooterLinks) {
      return (
        <div>
          <div className="trail-completed-modal_footer-header" style={{ margin: "10px 0 5px 0" }}>
            <div>Guided Learning</div>
            <a href="/trails">{this.props.trailsLink}</a>
          </div>
          <div style={{ margin: "0 0 10px 0" }}>Discover trails to kickstart your learning journey.</div>
        </div>
      );
    }
  };

  renderTrails = () => {
    if (!this.props.inProgressTrails.length) {
      return this.renderRecommendedTrails();
    }

    return this.renderInProgressTrails();
  };

  renderRecommendedTrails = () => {
    return <WebComponent tagName="th-recommended-trails" attrs={{ preferences: this.props.preferences }} />;
  };

  renderInProgressTrails = () => (
    <ul>
      {this.props.inProgressTrails.map((trail) => (
        <li className="trail-completed-modal_trail-list-item" key={trail.slug}>
          <img className="trail-completed-modal_icon-image--small" src={this.props.trailheadLogoUrl} alt="" />
          <div className="info">
            <div className="type">Trail</div>
            <a href={this.inProgressTrailLink(trail)}>{trail.title}</a>

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "15px" }}>
              <div className="points">+{trail.points}</div>

              <div style={{ display: "flex", alignItems: "center" }}>
                <span className="progress-label">{Math.floor(trail.progress)}%</span>
                <div className="progress-bar" style={{ width: "50px" }}>
                  <div className="progress-bar-inner" style={{ width: `${trail.progress || 0}%` }}></div>
                </div>
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );

  inProgressTrailLink = (trail) => {
    if (trail.audience === "employee") {
      return `/content/employee/trails/${trail.slug}`;
    }

    return `/content/learn/trails/${trail.slug}`;
  };

  render() {
    return (
      <IconContext>
        <div className="trail-completed-modal_outer">
          <th-celebration-modal
            ref={this.modal}
            open={this.state.isOpen}
            assistive-text="trail completed"
          >
            <div slot="page1" className="th-modal__content--full-screen">
              <div className="trail-completed-modal">
                <h1 className="th-celebration-modal__heading">
                  {this.props.celebrationHeader}
                </h1>
                <div>
                  <img
                    className="trail-completed-modal_icon-image"
                    src={this.props.trailheadLogoUrl}
                    alt="trail completed"
                  />
                </div>
                <div className="trail-completed-modal_title">{this.props.title}</div>

                <div>{this.renderSharing()}</div>
              </div>
            </div>

            <div slot="pageLast" className="th-modal__content--full-screen">
              <div className="trail-completed-modal">
                <div className="slds-p-around_medium trail-completed-modal_trails-list-page">
                  <h1 className="th-celebration-modal__heading">
                    {this.props.trailsListPageHeader}
                  </h1>
                  <p>
                    {this.props.trailsListPageDesc}
                  </p>
                  <div className="trail-completed-modal_trails-list-container">
                    {this.renderTrails()}
                  </div>
                </div>
              </div>
            </div>
          </th-celebration-modal>
        </div>
        {this.renderConfetti()}
      </IconContext>
    );
  }
}
